import { Component, Input, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SeoService } from '../core/services/seo.service';
import { StructuredDataService } from '../core/services/structured-data.service';
import { NavigationComponent } from '../shared/navigation.component';
import { FooterComponent } from '../shared/footer.component';

export interface BlogMetadata {
  title: string;
  description: string;
  author: string;
  publishDate: string;
  readTime: string;
  keywords: string;
  image: string;
  slug: string;
  category: string;
}

@Component({
  selector: 'app-blog-layout',
  standalone: true,
  imports: [CommonModule, RouterModule, NavigationComponent, FooterComponent],
  template: `
    <app-navigation></app-navigation>
    <div class="blog-container">
      <!-- Breadcrumb -->
      <nav class="breadcrumb-nav" aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li><a routerLink="/">🏠 Inicio</a></li>
          <li><a routerLink="/blogs">📖 Blog</a></li>
          <li class="current">{{metadata.title}}</li>
        </ol>
      </nav>

      <!-- Blog Header -->
      <header class="blog-header">
        <div class="blog-category">
          <span class="category-tag">{{metadata.category}}</span>
        </div>
        
        <h1 class="blog-title">{{metadata.title}}</h1>
        
        <div class="blog-meta">
          <div class="meta-item">
            <span class="meta-icon">👤</span>
            <span>{{metadata.author}}</span>
          </div>
          <div class="meta-item">
            <span class="meta-icon">📅</span>
            <time [attr.datetime]="metadata.publishDate">{{formatDate(metadata.publishDate)}}</time>
          </div>
          <div class="meta-item">
            <span class="meta-icon">⏱️</span>
            <span>{{metadata.readTime}} de lectura</span>
          </div>
        </div>

        <div class="blog-image-container">
          <img 
            [src]="metadata.image" 
            [alt]="metadata.title"
            class="blog-hero-image"
            loading="eager"
            [attr.width]="1200"
            [attr.height]="630"
          >
        </div>
      </header>

      <!-- Blog Content -->
      <main class="blog-content">
        <article class="blog-article">
          <ng-content></ng-content>
        </article>

        <!-- Share Section -->
        <section class="blog-share">
          <h3>📤 Comparte este artículo</h3>
          <div class="share-buttons">
            <button 
              (click)="shareOnWhatsApp()" 
              class="share-btn whatsapp"
              type="button"
              aria-label="Compartir en WhatsApp"
            >
              💬 WhatsApp
            </button>
            <button 
              (click)="shareOnLinkedIn()" 
              class="share-btn linkedin"
              type="button"
              aria-label="Compartir en LinkedIn"
            >
              💼 LinkedIn
            </button>
            <button 
              (click)="copyLink()" 
              class="share-btn copy"
              type="button"
              aria-label="Copiar enlace"
            >
              🔗 Copiar enlace
            </button>
          </div>
        </section>

        <!-- CTA Section -->
        <section class="blog-cta">
          <div class="cta-content">
            <h3>💡 ¿Te interesa implementar automatización en tu empresa?</h3>
            <p>En Pezeta Solutions ayudamos a empresas como la tuya a automatizar procesos y ahorrar tiempo valioso.</p>
            <div class="cta-buttons">
              <a 
                routerLink="/contacto" 
                class="cta-btn primary"
                aria-label="Solicitar consultoría gratuita"
              >
                📞 Consultoría Gratuita
              </a>
              <button 
                (click)="openWhatsApp()" 
                class="cta-btn secondary"
                type="button"
                aria-label="Contactar por WhatsApp"
              >
                💬 WhatsApp
              </button>
            </div>
          </div>
        </section>

        <!-- Related Articles -->
        <section class="related-articles">
          <h3>📚 Artículos relacionados</h3>
          <div class="related-grid">
            <article class="related-card">
              <h4>🚀 Próximamente: Sistemas ERP vs Software Tradicional</h4>
              <p>Descubre las ventajas de implementar un sistema ERP personalizado...</p>
            </article>
            <article class="related-card">
              <h4>📱 Próximamente: Apps Móviles para PyMEs</h4>
              <p>Cómo una aplicación móvil puede transformar tu negocio...</p>
            </article>
          </div>
        </section>
      </main>
    </div>

    <app-footer></app-footer>
  `,
  styleUrls: ['./blog-layout.component.css']
})
export class BlogLayoutComponent implements OnInit, OnDestroy {
  @Input() metadata!: BlogMetadata;

  constructor(
    private seoService: SeoService,
    private structuredDataService: StructuredDataService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit() {
    if (this.metadata) {
      this.updateSEO();
      this.addStructuredData();
    }
  }

  ngOnDestroy() {
    // Clean up structured data when component is destroyed
    this.structuredDataService.removeAllStructuredData();
  }

  private updateSEO() {
    const url = `https://chatbot.pezetasolutions.com/blogs/${this.metadata.slug}`;

    this.seoService.updateSEOData({
      title: `${this.metadata.title} | Blog Pezeta Solutions`,
      description: this.metadata.description,
      keywords: this.metadata.keywords,
      canonical: url,
      ogTitle: this.metadata.title,
      ogDescription: this.metadata.description,
      ogImage: `https://chatbot.pezetasolutions.com/${this.metadata.image}`,
      ogUrl: url,
      twitterTitle: this.metadata.title,
      twitterDescription: this.metadata.description,
      twitterImage: `https://chatbot.pezetasolutions.com/${this.metadata.image}`
    });
  }

  private addStructuredData() {
    const url = `https://chatbot.pezetasolutions.com/blogs/${this.metadata.slug}`;

    // Add Article schema for rich results
    this.structuredDataService.addArticleSchema({
      headline: this.metadata.title,
      description: this.metadata.description,
      url: url,
      datePublished: this.metadata.publishDate,
      dateModified: this.metadata.publishDate,
      author: this.metadata.author,
      image: `https://chatbot.pezetasolutions.com/${this.metadata.image}`,
      category: this.metadata.category
    });

    // Add breadcrumb schema
    this.structuredDataService.addBreadcrumbSchema([
      { name: 'Inicio', url: 'https://chatbot.pezetasolutions.com/' },
      { name: 'Blog', url: 'https://chatbot.pezetasolutions.com/blogs' },
      { name: this.metadata.title, url: url }
    ]);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  shareOnWhatsApp() {
    if (isPlatformBrowser(this.platformId)) {
      const url = encodeURIComponent(window.location.href);
      const text = encodeURIComponent(`${this.metadata.title} | Blog Pezeta Solutions`);
      window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
    }
  }

  shareOnLinkedIn() {
    if (isPlatformBrowser(this.platformId)) {
      const url = encodeURIComponent(window.location.href);
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
    }
  }

  copyLink() {
    if (isPlatformBrowser(this.platformId)) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        alert('¡Enlace copiado al portapapeles!');
      });
    }
  }

  openWhatsApp() {
    if (isPlatformBrowser(this.platformId)) {
      window.open('https://wa.me/message/GTV3AEMP6MFNC1', '_blank');
    }
  }
}